'use client';

import React, { PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DefaultAdults,
  DefaultChildren,
  MaxGuests,
  useSearch,
} from '@/context/listing-search-provider';
import {
  parseGuestCountsFromSearchParams,
} from '@/utils/guest-search-params';
import {
  CloseButton,
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import clsx from 'clsx';

import Stepper from '@/components/ui/stepper';

const GuestPickerPopover: React.FC<
  PropsWithChildren<{ maxGuests?: number; className?: string }>
> = ({ children, maxGuests, className }) => {
  const { adults: adultsFromContext, children: childrenCountFromContext, setAdults, setChildren, applySearchParams, searchParams } = useSearch();
  const adults = adultsFromContext ?? DefaultAdults;
  const childrenCount = childrenCountFromContext ?? DefaultChildren;
  const pickerMaxGuests = maxGuests || MaxGuests;
  const totalGuests = (adults ?? 0) + (childrenCount ?? 0);

  const hasChange = (() => {
    const { adults: urlAdults, children: urlChildren } =
      parseGuestCountsFromSearchParams(searchParams, {
        adults: DefaultAdults,
        children: DefaultChildren,
      });
    return adults !== urlAdults || childrenCount !== urlChildren;
  })();

  const handleApply = useCallback(() => {
    if (hasChange) {
      applySearchParams();
    }
  }, [hasChange, applySearchParams]);

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const openRef = useRef(false);

  // Sync Headless UI's open from render prop into state after commit (cannot setState during their render).
  useLayoutEffect(() => {
    if (openRef.current !== isOpen) setIsOpen(openRef.current);
  });

  // When popover is open, also close+apply on outside tap (capture phase).
  // Headless UI's PopoverBackdrop often doesn't receive touch on mobile.
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      handleApply();
    };
    document.addEventListener('click', handle, true);
    document.addEventListener('touchstart', handle, true);
    return () => {
      document.removeEventListener('click', handle, true);
      document.removeEventListener('touchstart', handle, true);
    };
  }, [isOpen, handleApply]);

  return (
    <Popover className={clsx('relative w-full', className)}>
      {({ open }) => {
        openRef.current = open;
        return (
          <>
            <div ref={triggerRef} className="w-full">
              <PopoverButton
                as="div"
                className={clsx('w-full cursor-pointer p-0 text-left')}
              >
                {children}
              </PopoverButton>
            </div>
            <PopoverBackdrop
              onClick={handleApply}
              transition
              className="fixed inset-0 z-10 h-full w-dvw"
            />
            <div ref={panelRef}>
              <PopoverPanel
                as="div"
                transition
                className="absolute right-0 z-10 mt-2 w-full rounded-xl border border-gray-0 bg-white p-4 shadow-lg"
              >
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex w-full items-center justify-between gap-4">
                    <div className="flex flex-col">
                      <label className="ut-sm font-medium text-gray-900">Adults</label>
                      <span className="ut-xs text-gray-7">Age 13+</span>
                    </div>
                    <Stepper
                      id="stepper-availability-adults"
                      stepDown={() => setAdults(Math.max(1, adults - 1))}
                      stepUp={() => {
                        if (totalGuests < pickerMaxGuests) {
                          setAdults(adults + 1);
                        }
                      }}
                      disableDown={adults <= 1}
                      disableUp={totalGuests >= pickerMaxGuests}
                      value={adults.toString()}
                    />
                  </div>
                  <div className="flex w-full items-center justify-between gap-4">
                    <div className="flex flex-col">
                      <label className="ut-sm font-medium text-gray-900">Children</label>
                      <span className="ut-xs text-gray-7">Age 2-12</span>
                    </div>
                    <Stepper
                      id="stepper-availability-children"
                      stepDown={() => setChildren(Math.max(0, childrenCount - 1))}
                      stepUp={() => {
                        if (totalGuests < pickerMaxGuests) {
                          setChildren(childrenCount + 1);
                        }
                      }}
                      disableDown={childrenCount <= 0}
                      disableUp={totalGuests >= pickerMaxGuests}
                      value={childrenCount.toString()}
                    />
                  </div>
                </div>
                {maxGuests && (
                  <p className="ut-xs mt-4 text-left font-normal text-gray-7">
                    There is a maximum of {maxGuests} guests allowed.
                  </p>
                )}
                <div className="flex justify-end">
                  <CloseButton
                    type="button"
                    className="ut-sm mt-4 w-fit p-0 font-normal text-gray-7 underline hover:text-black-1"
                    onClick={handleApply}
                  >
                    Close
                  </CloseButton>
                </div>
              </PopoverPanel>
            </div>
          </>
        );
      }}
    </Popover>
  );
};

export default GuestPickerPopover;
